<template>
  <header class="header" v-if="!$route.meta.hideHeader">
    <div class="container">
      <div class="header-wrapper">
        <!-- Logo Section -->
        <div class="logo-container">
          <img
            src="@/assets/image/AYH.webp"
            alt="安裕和國際企業社"
            class="logo"
          />

          <router-link to="/" class="company-info">
            <template v-if="currentLang === 'zh-TW' || currentLang === 'zh-CN'">
              <h1 class="company-name">安裕和國際企業社</h1>
              <span class="company-name-en">Ann Yoo HoH Company.</span>
            </template>

            <template v-else>
              <h1 class="company-name-en-only">Ann Yoo HoH Company.</h1>
            </template>
          </router-link>
        </div>

        <!-- Mobile Menu Button -->
        <button class="mobile-menu-btn" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <!-- Desktop Navigation -->
      <nav class="navigation">
        <ul class="nav-list">
          <li><router-link to="/" class="nav-link">主頁</router-link></li>

          <!-- 商品專區 - 子母下拉式選單 -->
          <li class="nav-dropdown products-dropdown">
            <a class="nav-link dropdown-trigger" href="#">
              商品專區
              <svg
                class="dropdown-arrow"
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M6 8l4 4 4-4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
            <div class="products-dropdown-menu">
              <div class="dropdown-menu-item">
                <router-link
                  to="/products"
                  class="dropdown-menu-link all-link"
                  @click="expandedCategoryId = null"
                >
                  所有分類
                </router-link>
              </div>

              <div
                v-for="mainCategory in categories"
                :key="mainCategory.MainCategoryId"
                class="dropdown-menu-item has-submenu"
                :class="{
                  active: expandedCategoryId === mainCategory.MainCategoryId,
                }"
              >
                <div
                  class="dropdown-menu-link main-category"
                  @click.stop="toggleCategory(mainCategory.MainCategoryId)"
                >
                  {{ mainCategory.MainCategoryName }}
                  <svg
                    class="arrow-icon"
                    :class="{
                      rotated:
                        expandedCategoryId === mainCategory.MainCategoryId,
                    }"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M7.5 5L12.5 10L7.5 15"
                      stroke="currentColor"
                      stroke-width="2"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  class="submenu"
                  v-show="expandedCategoryId === mainCategory.MainCategoryId"
                >
                  <router-link
                    v-for="subCategory in mainCategory.SubCategories"
                    :key="subCategory.SubCategoryId"
                    :to="`/products?main=${mainCategory.MainCategoryId}&sub=${subCategory.SubCategoryId}`"
                    class="submenu-link"
                    @click="expandedCategoryId = null"
                  >
                    {{ subCategory.SubCategoryName }}
                  </router-link>
                </div>
              </div>
            </div>
          </li>

          <li>
            <router-link to="/video" class="nav-link">
              產品影片展示
            </router-link>
          </li>

          <!-- 衛教專區 - 桌面版下拉選單 -->
          <li>
            <router-link to="/healthy" class="nav-link"> 衛教專區 </router-link>
          </li>

          <li>
            <router-link to="/contact" class="nav-link">聯絡客服</router-link>
          </li>

          <!-- User Login/Logout -->
          <li v-if="!isLoggedIn">
            <router-link to="/login" class="nav-link">註冊/登入</router-link>
          </li>

          <li v-else class="nav-dropdown">
            <a class="nav-link" href="#">會員專區</a>
            <ul class="dropdown-menu">
              <li><router-link to="/dashboard">前往</router-link></li>
              <!-- ✅ 改成會員中心 -->
              <li><a href="#" @click.prevent="logout">登出</a></li>
            </ul>
          </li>

          <!-- Font Size Selector -->
          <li class="font-size-selector-wrapper">
            <div
              class="font-size-selector-custom"
              @click="toggleFontSizeDropdown"
            >
              <span class="text">字體</span>
              <span class="arrow">▼</span>
            </div>
            <div v-if="showFontSizeDropdown" class="custom-font-size-dropdown">
              <div
                v-for="size in fontSizes"
                :key="size.value"
                class="font-size-option"
                :class="{ active: fontSize === size.value }"
                @click="changeFontSize(size.value)"
              >
                <span :style="{ fontSize: size.previewSize }">{{
                  size.label
                }}</span>
              </div>
            </div>
          </li>

          <!-- Language Selector -->
          <li class="language-selector-wrapper">
            <div id="google_translate_element" style="display: none"></div>
            <div
              class="language-selector-custom"
              @click="toggleLanguageDropdown"
            >
              <span class="text">語言</span>
              <span class="arrow">▼</span>
            </div>
            <div v-if="showLanguageDropdown" class="custom-language-dropdown">
              <div
                v-for="lang in languages"
                :key="lang.code"
                class="language-option"
                :class="{ active: currentLang === lang.code }"
                @click="changeLanguage(lang.code)"
              >
                {{ lang.name }}
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 主側邊選單遮罩 -->
    <div
      class="side-menu-overlay"
      :class="{ active: mobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>

    <!-- 主側邊選單 -->
    <div class="side-menu main-side-menu" :class="{ active: mobileMenuOpen }">
      <div class="side-menu-header">
        <button class="back-btn" @click="closeMobileMenu">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path
              d="M12.5 5L7.5 10L12.5 15"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>關閉選單</span>
        </button>
      </div>

      <div class="side-menu-content">
        <h3 class="side-menu-title">選單</h3>

        <!-- 首頁 -->
        <div class="menu-item-wrapper">
          <router-link
            to="/"
            class="menu-main-item"
            @click="handleMenuClick"
            :class="{ active: $route.path === '/' }"
          >
            首頁
          </router-link>

          <a
            href="#"
            @click.stop.prevent="toggleMobileProductsDropdown"
            class="menu-main-item with-arrow"
            :class="{ active: showMobileProductsDropdown }"
          >
            <span>商品專區</span>
            <svg
              class="arrow-icon"
              :class="{ 'arrow-down': showMobileProductsDropdown }"
              width="20"
              height="20"
              viewBox="0 0 20 20"
            >
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
          <div class="submenu-dropdown" v-if="showMobileProductsDropdown">
            <div class="submenu-item" @click="handleMenuClick">
              <router-link to="/products" class="submenu-link-text"
                >所有分類</router-link
              >
            </div>
            <div
              v-for="mainCategory in categories"
              :key="mainCategory.MainCategoryId"
              class="submenu-item"
            >
              <router-link
                :to="`/products?main=${mainCategory.MainCategoryId}`"
                class="submenu-link-text"
                @click="handleMenuClick"
              >
                {{ mainCategory.MainCategoryName }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- 產品影片展示 -->
        <div class="menu-item-wrapper">
          <router-link
            to="/video"
            class="menu-main-item"
            @click="handleMenuClick"
            :class="{ active: $route.path === '/video' }"
          >
            產品影片展示
          </router-link>
        </div>

        <!-- 衛教專區 -->
        <div class="menu-item-wrapper">
          <router-link
            to="/healthy"
            class="menu-main-item"
            @click="handleMenuClick"
            :class="{ active: $route.path.startsWith('/healthy') }"
          >
            衛教專區
          </router-link>
        </div>

        <!-- 聯絡客服 -->
        <div class="menu-item-wrapper">
          <router-link
            to="/contact"
            class="menu-main-item"
            @click="handleMenuClick"
            :class="{ active: $route.path === '/contact' }"
          >
            聯絡客服
          </router-link>
        </div>

        <!-- 會員相關 -->
        <div class="menu-section-divider"></div>
        <template v-if="isLoggedIn">
          <div class="menu-item-wrapper">
            <router-link
              to="/dashboard"
              class="menu-main-item"
              @click="handleMenuClick"
            >
              會員專區
            </router-link>
          </div>
          <div class="menu-item-wrapper">
            <a
              href="#"
              @click.prevent="logout"
              class="menu-main-item logout-item"
            >
              登出
            </a>
          </div>
        </template>
        <template v-else>
          <div class="menu-item-wrapper">
            <router-link
              to="/login"
              class="menu-main-item"
              @click="handleMenuClick"
              :class="{ active: $route.path === '/login' }"
            >
              註冊/登入
            </router-link>
          </div>
        </template>

        <!-- 設定區域 -->
        <div class="menu-section-divider"></div>

        <!-- 語言選擇 -->
        <div class="menu-item-wrapper">
          <a
            href="#"
            @click.stop.prevent="toggleMobileLanguageDropdown"
            class="menu-main-item with-arrow"
            :class="{ active: showMobileLanguageDropdown }"
          >
            <span>語言 / Language</span>
            <svg
              class="arrow-icon"
              :class="{ 'arrow-down': showMobileLanguageDropdown }"
              width="20"
              height="20"
              viewBox="0 0 20 20"
            >
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
          <div class="submenu-dropdown" v-if="showMobileLanguageDropdown">
            <div
              v-for="lang in languages"
              :key="lang.code"
              class="submenu-item"
              :class="{ active: currentLang === lang.code }"
              @click="changeLanguage(lang.code)"
            >
              {{ lang.name }}
            </div>
          </div>
        </div>

        <!-- 字體選擇 -->
        <div class="menu-item-wrapper">
          <a
            href="#"
            @click.stop.prevent="toggleMobileFontSizeDropdown"
            class="menu-main-item with-arrow"
            :class="{ active: showMobileFontSizeDropdown }"
          >
            <span>字體 / Font Size</span>
            <svg
              class="arrow-icon"
              :class="{ 'arrow-down': showMobileFontSizeDropdown }"
              width="20"
              height="20"
              viewBox="0 0 20 20"
            >
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
          <div class="submenu-dropdown" v-if="showMobileFontSizeDropdown">
            <div
              v-for="size in fontSizes"
              :key="size.value"
              class="submenu-item"
              :class="{ active: fontSize === size.value }"
              @click="changeFontSize(size.value)"
            >
              <span :style="{ fontSize: size.previewSize }">{{
                size.label
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 商品專區側邊選單 -->
    <div
      class="side-menu-overlay secondary"
      :class="{ active: productsMenuOpen }"
      @click="closeProductsMenu"
    ></div>
    <div
      class="side-menu secondary-side-menu"
      :class="{ active: productsMenuOpen }"
    >
      <div class="side-menu-header">
        <button class="back-btn" @click="closeProductsMenu">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path
              d="M12.5 5L7.5 10L12.5 15"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>返回 所有分類</span>
        </button>
      </div>

      <div class="category-section">
        <h4 class="category-title">全部商品</h4>
        <router-link to="/products" @click="handleCategoryClick">
          查看全部商品
        </router-link>
      </div>

      <div
        class="category-section"
        v-for="(cat, index) in productCategories"
        :key="index"
      >
        <h4 class="category-title">{{ cat.title }}</h4>

        <router-link
          v-for="(item, i) in cat.items"
          :key="i"
          :to="item.link"
          @click="handleCategoryClick"
        >
          {{ item.label }}
        </router-link>
      </div>
    </div>

    <!-- 衛教專區側邊選單 -->
    <div
      class="side-menu-overlay secondary"
      :class="{ active: educationMenuOpen }"
      @click="closeEducationMenu"
    ></div>
    <div
      class="side-menu secondary-side-menu"
      :class="{ active: educationMenuOpen }"
    >
      <div class="side-menu-header">
        <button class="back-btn" @click="closeEducationMenu">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path
              d="M12.5 5L7.5 10L12.5 15"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>返回 所有分類</span>
        </button>
      </div>
      <div class="side-menu-content">
        <h3 class="side-menu-title">所有 衛教專區</h3>

        <div class="category-section">
          <h4 class="category-title">復健照護</h4>
          <a href="/education/rehabilitation/joint" @click="handleCategoryClick"
            >關節活動照護</a
          >
          <a
            href="/education/rehabilitation/postop"
            @click="handleCategoryClick"
            >術後復健指南</a
          >
          <a href="/education/rehabilitation/home" @click="handleCategoryClick"
            >居家復健運動</a
          >
        </div>

        <div class="category-section">
          <h4 class="category-title">輔具使用</h4>
          <a href="/education/equipment/wheelchair" @click="handleCategoryClick"
            >輪椅使用須知</a
          >
          <a href="/education/equipment/walker" @click="handleCategoryClick"
            >助行器操作指南</a
          >
          <a
            href="/education/equipment/maintenance"
            @click="handleCategoryClick"
            >輔具保養維護</a
          >
        </div>

        <div class="category-section">
          <h4 class="category-title">健康管理</h4>
          <a
            href="/education/health/blood-pressure"
            @click="handleCategoryClick"
            >血壓監測須知</a
          >
          <a href="/education/health/blood-sugar" @click="handleCategoryClick"
            >血糖控制指南</a
          >
          <a href="/education/health/home-care" @click="handleCategoryClick"
            >居家照護技巧</a
          >
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 2rem);
}

.header {
  background: #ffffff;
  color: white;
  padding: clamp(0.75rem, 1.5vh, 1.25rem) 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: clamp(60px, 10vh, 80px);
}

.logo-container {
  display: flex;
  align-items: center;
  gap: clamp(0.75rem, 1.5vw, 1rem);
  flex-shrink: 0;
  max-width: calc(100% - 60px);
}

@media (min-width: 1024px) {
  .logo-container {
    max-width: none;
  }
}

.logo {
  width: clamp(50px, 8vw, 100px);
  height: auto;
  flex-shrink: 0;
}

.company-info {
  text-decoration: none;
  color: #3b3b7e;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.company-name {
  font-size: clamp(1.2rem, 2.5vw, 1.5rem);
  font-weight: bold;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

@media (min-width: 1024px) {
  .company-name {
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
  }
}

.company-name-en {
  font-size: clamp(0.85rem, 1.5vw, 0.9rem);
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

@media (min-width: 1024px) {
  .company-name-en {
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
  }
}

.company-name-en-only {
  font-size: clamp(1.2rem, 2.5vw, 2rem);
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.navigation {
  display: none;
}

@media (min-width: 1024px) {
  .navigation {
    display: block;
    flex: 0 0 auto;
  }
}

.mobile-menu-btn {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: clamp(26px, 4vw, 28px);
  height: clamp(22px, 3.5vw, 24px);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .mobile-menu-btn {
    display: none;
  }
}

.mobile-menu-btn span {
  width: 100%;
  height: clamp(2px, 0.4vh, 3px);
  background-color: #3b3b7e;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.side-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.6);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 2000;
  touch-action: none;
}

.side-menu-overlay.active {
  opacity: 1;
  visibility: visible;
}

.side-menu-overlay.secondary {
  z-index: 2200;
}

.side-menu-overlay.secondary.active {
  opacity: 1;
  visibility: visible;
}

@media (min-width: 1024px) {
  .side-menu-overlay {
    display: none;
  }
}

.side-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: min(420px, 90vw);
  height: 100vh;
  height: 100dvh;
  background: #ffffff;
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2100;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.side-menu.active {
  right: 0;
}

.side-menu.secondary-side-menu {
  z-index: 2300;
}

@media (min-width: 1024px) {
  .side-menu {
    display: none;
  }
}

.side-menu-header {
  padding: clamp(1rem, 2vh, 1.25rem) clamp(1.5rem, 3vw, 1.75rem);
  background: linear-gradient(135deg, #3b3b7e 0%, #4a4a9e 100%);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.back-btn {
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1.5vw, 0.75rem);
  background: none;
  border: none;
  color: #ffffff;
  font-size: clamp(1rem, 2vw, 1.125rem);
  font-weight: 500;
  cursor: pointer;
  padding: clamp(0.4rem, 1vh, 0.5rem) 0;
  transition: all 0.3s ease;
  width: 100%;
}

.back-btn:hover {
  opacity: 0.85;
  transform: translateX(-3px);
}

.back-btn:active {
  transform: translateX(-5px);
}

.back-btn svg {
  flex-shrink: 0;
  width: clamp(18px, 3vw, 22px);
  height: clamp(18px, 3vw, 22px);
}

.side-menu-content {
  padding: clamp(1.5rem, 3vh, 2rem) clamp(1.5rem, 3vw, 1.75rem);
  flex: 1;
  overflow-y: auto;
}

.side-menu-title {
  font-size: clamp(0.8rem, 1.5vw, 0.875rem);
  color: #6b7280;
  margin: 0 0 clamp(1.5rem, 3vh, 2rem) 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.menu-item-wrapper {
  margin-bottom: clamp(0.4rem, 1vh, 0.5rem);
}

.menu-main-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(0.9rem, 1.5vh, 1rem) clamp(0.9rem, 2vw, 1rem);
  color: #4b5563;
  text-decoration: none;
  font-size: clamp(1rem, 2vw, 1.0625rem);
  font-weight: 500;
  transition: all 0.25s ease;
  border-radius: clamp(6px, 1vw, 8px);
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.menu-main-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: clamp(3px, 0.6vw, 4px);
  height: 0;
  background: #3b3b7e;
  transition: height 0.25s ease;
  border-radius: 0 2px 2px 0;
}

.menu-main-item:hover,
.menu-main-item.active {
  color: #3b3b7e;
  background: #f3f4f6;
  padding-left: clamp(1.3rem, 2.5vw, 1.5rem);
}

.menu-main-item:hover::before,
.menu-main-item.active::before {
  height: 70%;
}

.menu-main-item:active {
  background: #e5e7eb;
  transform: scale(0.98);
}

.menu-main-item.with-arrow {
  padding-right: clamp(0.6rem, 1.5vw, 0.75rem);
}

.menu-main-item .arrow-icon {
  flex-shrink: 0;
  opacity: 0.6;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.menu-main-item.active .arrow-icon,
.menu-main-item:hover .arrow-icon {
  opacity: 1;
}

.menu-main-item .arrow-icon.arrow-down {
  transform: rotate(90deg);
}

.menu-main-item.logout-item {
  color: #dc2626;
}

.menu-main-item.logout-item:hover {
  color: #b91c1c;
  background: #fef2f2;
}

.menu-main-item.logout-item::before {
  background: #dc2626;
}

.submenu-dropdown {
  background-color: #f9fafb;
  border-radius: clamp(6px, 1vw, 8px);
  margin-top: clamp(0.4rem, 1vh, 0.5rem);
  overflow: hidden;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

.submenu-item {
  padding: clamp(0.6rem, 1.2vh, 0.75rem) clamp(1.2rem, 2.5vw, 1.5rem);
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: clamp(0.9rem, 1.8vw, 0.95rem);
}

.submenu-item:hover {
  background-color: #f3f4f6;
  color: #3b3b7e;
  padding-left: clamp(1.6rem, 3vw, 2rem);
}

.submenu-item.active {
  background-color: #e5e7eb;
  color: #3b3b7e;
  font-weight: 600;
}

.menu-section-divider {
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    #e5e7eb 20%,
    #e5e7eb 80%,
    transparent
  );
  margin: clamp(1.2rem, 2vh, 1.5rem) 0;
}

.category-section {
  margin-bottom: clamp(2rem, 4vh, 2.5rem);
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-title {
  font-size: clamp(1.15rem, 2.2vw, 1.25rem);
  color: #1f2937;
  font-weight: 700;
  margin: 0 0 clamp(0.8rem, 1.5vh, 1rem) 0;
  padding-bottom: clamp(0.6rem, 1vh, 0.75rem);
  border-bottom: 3px solid #e5e7eb;
  letter-spacing: 0.3px;
}

.category-section a {
  display: block;
  padding: clamp(0.9rem, 1.5vh, 1rem) clamp(0.9rem, 2vw, 1rem);
  color: #4b5563;
  text-decoration: none;
  font-size: clamp(1rem, 2vw, 1.0625rem);
  font-weight: 500;
  transition: all 0.25s ease;
  border-radius: clamp(6px, 1vw, 8px);
  margin-bottom: clamp(0.4rem, 1vh, 0.5rem);
  position: relative;
  overflow: hidden;
}

.category-section a::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: clamp(3px, 0.6vw, 4px);
  height: 0;
  background: #3b3b7e;
  transition: height 0.25s ease;
  border-radius: 0 2px 2px 0;
}

.category-section a:hover {
  color: #3b3b7e;
  background: #f3f4f6;
  padding-left: clamp(1.3rem, 2.5vw, 1.5rem);
}

.category-section a:hover::before {
  height: 70%;
}

.category-section a:active {
  background: #e5e7eb;
  transform: scale(0.98);
}

/* Desktop Navigation */
@media (min-width: 1024px) {
  .header .container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1.5rem, 3vw, 2rem);
  }

  .header-wrapper {
    flex: 0 1 auto;
    min-width: 0;
  }

  .nav-list {
    display: flex;
    list-style: none;
    align-items: center;
    gap: clamp(0.4rem, 1vw, 0.5rem);
    flex-wrap: wrap;
  }

  .nav-list > li {
    position: relative;
    white-space: nowrap;
  }

  .nav-link {
    color: #3b3b7e;
    text-decoration: none;
    padding: clamp(0.5rem, 1vh, 0.6rem) clamp(0.6rem, 1.2vw, 0.8rem);
    display: block;
    transition: all 0.3s ease;
    border-radius: clamp(5px, 0.8vw, 6px);
    font-size: clamp(1rem, 1.5vw, 1.15rem);
    font-weight: bold;
    position: relative;
  }

  .nav-link:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .nav-link::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 0;
    height: clamp(2px, 0.4vh, 3px);
    background-color: #3b3b7e;
    transition: width 0.3s ease;
  }

  .nav-link:hover::after {
    width: 100%;
  }

  .nav-link.router-link-active::after {
    width: 100%;
  }

  .nav-dropdown {
    position: relative;
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: clamp(6px, 1vw, 8px);
    min-width: 100px;
    list-style: none;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    z-index: 100;
    margin-top: clamp(0.4rem, 0.8vh, 0.5rem);
    text-align: center;
  }

  .nav-dropdown:hover .dropdown-menu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .dropdown-menu li a {
    color: #333;
    padding: clamp(0.6rem, 1vh, 0.75rem) clamp(1rem, 1.8vw, 1.25rem);
    display: block;
    text-decoration: none;
    transition: all 0.3s ease;
    white-space: nowrap;
    font-size: clamp(0.9rem, 1.3vw, 1rem);
  }

  .dropdown-menu li a:hover {
    background-color: #f5f5f5;
    color: #667eea;
  }

  .dropdown-menu li:first-child a {
    border-radius: clamp(6px, 1vw, 8px) clamp(6px, 1vw, 8px) 0 0;
  }

  .dropdown-menu li:last-child a {
    border-radius: 0 0 clamp(6px, 1vw, 8px) clamp(6px, 1vw, 8px);
  }

  .language-selector-wrapper {
    position: relative;
  }

  .font-size-selector-wrapper {
    display: none !important;
  }

  .language-selector-custom,
  .font-size-selector-custom {
    display: flex;
    align-items: center;
    gap: clamp(0.3rem, 0.6vw, 0.4rem);
    padding: clamp(0.5rem, 1vh, 0.6rem) clamp(0.6rem, 1.2vw, 0.8rem);
    cursor: pointer;
    border-radius: clamp(5px, 0.8vw, 6px);
    transition: all 0.3s ease;
    color: #3b3b7e;
    font-size: clamp(1rem, 1.5vw, 1.15rem);
    font-weight: 500;
    white-space: nowrap;
    font-weight: bold;
  }

  .language-selector-custom:hover,
  .font-size-selector-custom:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .language-selector-custom .arrow,
  .font-size-selector-custom .arrow {
    font-size: 0.7em;
    transition: transform 0.3s ease;
  }

  .custom-language-dropdown,
  .custom-font-size-dropdown {
    position: absolute;
    top: 100%;
    right: -15px;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: clamp(6px, 1vw, 8px);
    min-width: clamp(90px, 15vw, 100px);
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    transition: all 0.3s ease;
    z-index: 100;
    margin-top: clamp(0.4rem, 0.8vh, 0.5rem);
    max-height: clamp(250px, 40vh, 325px);
    overflow-y: auto;
    font-size: clamp(14px, 1.3vw, 15px);
  }

  .language-option,
  .font-size-option {
    padding: clamp(0.6rem, 1vh, 0.75rem) clamp(1rem, 1.8vw, 1.25rem);
    cursor: pointer;
    transition: all 0.3s ease;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .language-option.active,
  .font-size-option.active {
    background-color: #667eea;
    color: white;
    font-weight: bold;
  }

  .language-option:first-child,
  .font-size-option:first-child {
    border-radius: clamp(6px, 1vw, 8px) clamp(6px, 1vw, 8px) 0 0;
  }

  .language-option:last-child,
  .font-size-option:last-child {
    border-radius: 0 0 clamp(6px, 1vw, 8px) clamp(6px, 1vw, 8px);
  }

  .products-dropdown {
    position: relative;
    margin-left: clamp(0.5rem, 1vw, 1rem); /* ✅ 新增：加左右外邊距 */
  }

  .dropdown-trigger {
    display: flex !important;
    align-items: center;
    gap: clamp(0.2rem, 0.5vw, 0.3rem);
    justify-content: center; /* ✅ 新增：讓內容置中 */
  }

  .dropdown-trigger .dropdown-arrow {
    transition: transform 0.3s ease;
  }

  .products-dropdown:hover .dropdown-arrow {
    transform: rotate(180deg);
  }

  .products-dropdown-menu {
    position: absolute;
    top: 100%;
    left: clamp(-40px, -3vw, -10px);
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: clamp(6px, 1vw, 8px);
    min-width: 200px;
    max-width: clamp(280px, 35vw, 350px);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    z-index: 100;
    margin-top: clamp(0.4rem, 0.8vh, 0.5rem);
    padding: clamp(0.5rem, 1vh, 0.75rem);
    max-height: 70vh;
    overflow-y: auto;
  }

  .products-dropdown:hover .products-dropdown-menu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .dropdown-menu-item {
    margin-bottom: clamp(0.3rem, 0.5vh, 0.4rem);
  }

  .dropdown-menu-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: clamp(0.7rem, 1vh, 0.85rem) clamp(0.8rem, 1.2vw, 1rem);
    color: #333;
    text-decoration: none;
    font-size: clamp(13px, 1.1vw, 14px);
    font-weight: 500;
    transition: all 0.3s ease;
    border-radius: clamp(4px, 0.6vw, 5px);
    cursor: pointer;
  }

  .dropdown-menu-link.all-link {
    background: #f0f0f5;
    color: #3b3b7e;
    font-weight: 600;
    border: 1px solid #e5e7eb;
  }

  .dropdown-menu-link.all-link:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }

  .dropdown-menu-link.main-category {
    background: transparent;
    user-select: none;
  }

  .dropdown-menu-link.main-category:active {
    transform: scale(0.98);
  }

  .dropdown-menu-link:hover {
    background: #f3f4f6;
    color: #3b3b7e;
  }

  .dropdown-menu-link .arrow-icon {
    flex-shrink: 0;
    transition: transform 0.3s ease;
    opacity: 0.7;
  }

  .dropdown-menu-link .arrow-icon.rotated {
    transform: rotate(90deg);
    opacity: 1;
  }

  .submenu {
    display: none;
    background: #3b3b7e; /* 白色 */
    border-radius: clamp(4px, 0.6vw, 5px);
    margin-top: clamp(0.3rem, 0.5vh, 0.4rem);
    padding: clamp(0.3rem, 0.5vh, 0.4rem) 0;
    animation: slideDown 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); /* 加個陰影 */
  }

  .dropdown-menu-item.has-submenu.active .submenu {
    display: block;
  }

  .dropdown-menu-item.has-submenu.active .dropdown-menu-link {
    background: rgba(255, 255, 255, 0.15);
    color: #333;
  }

  .submenu-link {
    display: block;
    padding: clamp(0.6rem, 0.9vh, 0.7rem) clamp(1rem, 1.5vw, 1.3rem);
    color: #ffffff;
    text-decoration: none;
    font-size: clamp(12px, 1vw, 13px);
    transition: all 0.3s ease;
    border-radius: clamp(3px, 0.5vw, 4px);
    margin: 0 clamp(0.3rem, 0.5vw, 0.4rem);
  }

  .submenu-link:hover {
    background: rgba(59, 59, 126, 0.4);
    color: #fff;
    padding-left: clamp(1.3rem, 1.8vw, 1.6rem);
  }

  /* 多語言版面調整 */
  body.layout-th .header .container,
  body.layout-en .header .container,
  body.layout-ja .header .container,
  body.layout-vi .header .container,
  body.layout-ko .header .container {
    display: flex;
    flex-direction: column;
    gap: clamp(0.8rem, 1.5vh, 1rem);
  }

  body.layout-th .header-wrapper,
  body.layout-en .header-wrapper,
  body.layout-ja .header-wrapper,
  body.layout-vi .header-wrapper {
    width: 100%;
    justify-content: center;
  }

  body.layout-th .navigation,
  body.layout-en .navigation,
  body.layout-ja .navigation,
  body.layout-vi .navigation {
    width: 100%;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding-top: 0rem;
    margin-top: clamp(-12px, -1.5vh, -10px);
  }

  body.layout-th .nav-list,
  body.layout-en .nav-list,
  body.layout-ja .nav-list,
  body.layout-vi .nav-list {
    justify-content: center;
    gap: clamp(0.4rem, 0.8vw, 0.5rem);
  }

  body.layout-th .nav-link,
  body.layout-en .nav-link,
  body.layout-ja .nav-link,
  body.layout-vi .nav-link {
    padding: clamp(0.5rem, 1vh, 0.6rem) clamp(0.6rem, 1.2vw, 0.8rem);
    font-size: clamp(0.85rem, 1.3vw, 0.9rem);
  }

  body.layout-th .language-selector-custom,
  body.layout-th .font-size-selector-custom,
  body.layout-en .language-selector-custom,
  body.layout-en .font-size-selector-custom,
  body.layout-ja .language-selector-custom,
  body.layout-ja .font-size-selector-custom,
  body.layout-vi .language-selector-custom,
  body.layout-vi .font-size-selector-custom {
    padding: clamp(0.5rem, 1vh, 0.6rem) clamp(0.6rem, 1.2vw, 0.8rem);
    font-size: clamp(0.85rem, 1.3vw, 0.9rem);
  }
}

/* 商品專區 dropdown 列表項目的連結文字 */
.submenu-link-text {
  display: block;
  color: inherit;
  text-decoration: none;
  transition: all 0.3s ease;
  width: 100%;
}

.submenu-item .submenu-link-text:hover {
  color: #3b3b7e;
}

/* 優化 menu-item-wrapper 中的 submenu-dropdown */
.menu-item-wrapper .submenu-dropdown {
  margin-bottom: clamp(0.4rem, 1vh, 0.5rem);
}

/* 平板/中型設備上的優化 */
@media (max-width: 1023px) and (min-width: 600px) {
  .menu-main-item {
    padding: clamp(0.85rem, 1.4vh, 0.95rem) clamp(0.85rem, 1.8vw, 0.95rem);
    font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  }

  .submenu-item {
    padding: clamp(0.55rem, 1vh, 0.7rem) clamp(1rem, 2.3vw, 1.4rem);
    font-size: clamp(0.85rem, 1.6vw, 0.9rem);
  }
}

/* 小型設備（手機）優化 */
@media (max-width: 599px) {
  .menu-main-item {
    padding: clamp(0.8rem, 1.3vh, 0.9rem) clamp(0.8rem, 1.5vw, 0.9rem);
    font-size: clamp(0.9rem, 1.6vw, 1rem);
  }

  .submenu-item {
    padding: clamp(0.5rem, 1vh, 0.65rem) clamp(1rem, 2vw, 1.2rem);
    font-size: clamp(0.8rem, 1.4vw, 0.9rem);
  }

  .submenu-item:hover {
    padding-left: clamp(1.4rem, 2.5vw, 1.8rem);
  }
}
</style>

<style>
body > .skiptranslate,
.goog-te-banner-frame,
.goog-logo-link,
.goog-te-gadget,
.goog-te-gadget-simple,
#goog-gt-tt,
.goog-te-menu-frame {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

body {
  top: 0 !important;
  position: static !important;
}
</style>

<script>
import api from "@/assets/js/utils/api";
import { showSuccess, showError } from "@/assets/js/utils/alertService";

export default {
  name: "AppNavbar",
  data() {
    return {
      showMobileProductsDropdown: false,
      mobileMenuOpen: false,
      showLanguageDropdown: false,
      showFontSizeDropdown: false,
      showMobileLanguageDropdown: false,
      showMobileFontSizeDropdown: false,
      currentLang: "zh-TW",
      fontSize: "medium",
      isLoggedIn: false,
      productsMenuOpen: false,
      productsDropdownOpen: false,
      scrollPosition: 0,
      categories: [], // 動態分類數據
      expandedCategoryId: null, // 追蹤展開的主分類 ID

      languages: [
        { code: "zh-TW", name: "繁體中文" },
        { code: "en", name: "English" },
        { code: "ja", name: "日本語" },
        { code: "ko", name: "한국어" },
        { code: "vi", name: "Tiếng Việt" },
        { code: "zh-CN", name: "简体中文" },
        { code: "th", name: "ภาษาไทย" },
      ],
      fontSizes: [
        { value: "large", label: "大", previewSize: "16px" },
        { value: "extra-large", label: "特大", previewSize: "18px" },
      ],
      productCategories: [],
    };
  },
  mounted() {
    this.checkLoginStatus();
    this.loadCategories();

    this.loadFontSizePreference();
    this.loadLanguagePreference();
    this.initGoogleTranslate();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    this.unlockBodyScroll();
  },
  methods: {
    async loadCategories() {
      try {
        const res = await api.get("/category/all");
        console.log("API Response:", res.data);
        if (res.data?.success && res.data?.data) {
          // 按照 Sequence 排序主分類
          this.categories = res.data.data
            // ✅ 只取啟用主分類
            .filter((m) => m.IsActive)
            .sort((a, b) => a.Sequence - b.Sequence)
            .map((m) => ({
              ...m,
              // ✅ 只取啟用子分類
              SubCategories: Array.isArray(m.SubCategories)
                ? m.SubCategories.filter((s) => s.IsActive).sort(
                    (a, b) => a.Sequence - b.Sequence,
                  )
                : [],
            }));

          // 同時排序子分類
          this.categories.forEach((cat) => {
            if (cat.SubCategories && Array.isArray(cat.SubCategories)) {
              cat.SubCategories.sort((a, b) => a.Sequence - b.Sequence);
            }
          });
          console.log("Loaded categories:", this.categories);
          console.log("Categories count:", this.categories.length);
        } else {
          console.log("No data or unsuccessful response");
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
        console.error("Error details:", error.response);
      }
    },
    toggleCategory(categoryId) {
      // 如果點擊已展開的分類，則收合；否則展開新的分類
      if (this.expandedCategoryId === categoryId) {
        this.expandedCategoryId = null;
      } else {
        this.expandedCategoryId = categoryId;
      }
    },
    async checkLoginStatus() {
      try {
        const res = await api.get("/front/customer/verify");
        this.isLoggedIn = res.data?.success === true;
      } catch (err) {
        // ✅ 關鍵：無論什麼錯誤都要設為 false
        console.log("登入驗證失敗，設為未登入狀態:", err.response?.status);
        this.isLoggedIn = false;

        // ✅ 如果是 401，清除可能殘留的無效 cookie
        if (err.response?.status === 401) {
          document.cookie =
            "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        }
      }
    },
    async logout() {
      try {
        await api.post("/front/customer/logout"); // 後端清除 cookie
        this.isLoggedIn = false;
        showSuccess("已成功登出");
        this.$router.push("/login");
        this.closeMobileMenu();
      } catch (error) {
        showError("登出失敗，請稍後再試。");
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      if (this.mobileMenuOpen) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
        this.showMobileLanguageDropdown = false;
        this.showMobileFontSizeDropdown = false;
      }
    },

    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.unlockBodyScroll();
      this.showMobileLanguageDropdown = false;
      this.showMobileFontSizeDropdown = false;
      this.showMobileProductsDropdown = false;
    },

    handleMenuClick() {
      this.closeMobileMenu();
    },
    openProductsMenu() {
      this.productsMenuOpen = true;
      this.educationMenuOpen = false;
      this.mobileMenuOpen = false;
    },
    closeProductsMenu() {
      this.productsMenuOpen = false;
      this.mobileMenuOpen = true;
    },
    lockBodyScroll() {
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = "100%";
      document.documentElement.style.overflow = "hidden";
    },
    unlockBodyScroll() {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.documentElement.style.overflow = "";

      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
        this.scrollPosition = 0;
      }
    },
    handleCategoryClick() {
      this.productsMenuOpen = false;
      this.educationMenuOpen = false;
      this.mobileMenuOpen = false;
      this.unlockBodyScroll();
    },
    showProductsDropdown() {
      if (window.innerWidth >= 1024) {
        this.productsDropdownOpen = true;
      }
    },
    hideProductsDropdown() {
      if (window.innerWidth >= 1024) {
        this.productsDropdownOpen = false;
      }
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
      this.showFontSizeDropdown = false;
    },
    toggleFontSizeDropdown() {
      this.showFontSizeDropdown = !this.showFontSizeDropdown;
      this.showLanguageDropdown = false;
    },
    toggleMobileLanguageDropdown() {
      this.showMobileLanguageDropdown = !this.showMobileLanguageDropdown;
      this.showMobileFontSizeDropdown = false;
    },
    toggleMobileFontSizeDropdown() {
      this.showMobileFontSizeDropdown = !this.showMobileFontSizeDropdown;
      this.showMobileLanguageDropdown = false;
    },
    handleClickOutside(event) {
      const langSelector = event.target.closest(
        ".language-selector-wrapper, .language-selector-custom",
      );
      const fontSelector = event.target.closest(
        ".font-size-selector-wrapper, .font-size-selector-custom",
      );
      const productsDropdown = event.target.closest(".products-dropdown");

      if (!langSelector) this.showLanguageDropdown = false;
      if (!fontSelector) this.showFontSizeDropdown = false;
      if (!productsDropdown) this.expandedCategoryId = null;
    },
    changeLanguage(langCode) {
      this.currentLang = langCode;
      localStorage.setItem("preferredLanguage", langCode);
      this.applyLayoutByLanguage();
      this.showLanguageDropdown = false;
      this.showMobileLanguageDropdown = false;

      const select = document.querySelector(".goog-te-combo");
      if (select) {
        select.value = langCode;
        select.dispatchEvent(new Event("change"));
      }
    },
    loadLanguagePreference() {
      const saved = localStorage.getItem("preferredLanguage");
      this.currentLang = saved || "zh-TW";
      this.applyLayoutByLanguage();

      if (saved && saved !== "zh-TW") {
        setTimeout(() => {
          const select = document.querySelector(".goog-te-combo");
          if (select) {
            select.value = saved;
            select.dispatchEvent(new Event("change"));
          }
        }, 500);
      }
    },
    applyLayoutByLanguage() {
      const lang = this.currentLang;
      document.body.classList.remove(
        "layout-th",
        "layout-en",
        "layout-ja",
        "layout-vi",
        "layout-ko",
      );

      if (["th", "en", "ja", "vi", "ko"].includes(lang)) {
        document.body.classList.add(`layout-${lang}`);
      }
    },
    changeFontSize(size) {
      this.fontSize = size;
      localStorage.setItem("preferredFontSize", size);
      this.showFontSizeDropdown = false;
      this.showMobileFontSizeDropdown = false;

      document.documentElement.classList.remove(
        "font-size-large",
        "font-size-extra-large",
      );
      document.documentElement.classList.add(`font-size-${size}`);
    },
    loadFontSizePreference() {
      const saved = localStorage.getItem("preferredFontSize");
      this.fontSize = saved || "medium";

      document.body.classList.remove(
        "font-size-large",
        "font-size-extra-large",
      );
      document.body.classList.add(`font-size-${this.fontSize}`);
    },
    initGoogleTranslate() {
      if (document.getElementById("google-translate-script")) return;

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "zh-TW",
            includedLanguages: "zh-TW,en,ja,ko,vi,zh-CN,th",
            layout:
              window.google.translate.TranslateElement.InlineLayout.VERTICAL,
            autoDisplay: false,
          },
          "google_translate_element",
        );

        this.loadLanguagePreference();
      };

      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.head.appendChild(script);
    },

    toggleMobileProductsDropdown() {
      this.showMobileProductsDropdown = !this.showMobileProductsDropdown;
      this.showMobileLanguageDropdown = false;
      this.showMobileFontSizeDropdown = false;
    },
  },

  watch: {
    $route() {
      // 路由變化時重新檢查登入狀態
      this.checkLoginStatus();
    },
  },
};
</script>
